import {
  ReactElement,
  ReactNode,
  createContext,
  useContext,
  useMemo,
} from 'react';
import {
  Machine,
  MachineConnectionType,
  useGetMachineQuery,
} from '../../api/graphql/generated';
import { ScratchpadPlaylistContextProvider } from '../../components/machine/playlist/scratchpad-playlist.hooks';

export interface MachineContextValue {
  machine: Machine;
  isDirectConnect: boolean;
  hideAttachments?: boolean;
  hideRecommendations?: boolean;
}

export const MachineContext = createContext<MachineContextValue | undefined>(
  undefined
);

export interface MachineContextProviderProps {
  machineId: string;
  teamId: string;
  children: ReactNode;
}

export const MachineContextProvider = (
  props: MachineContextProviderProps
): ReactElement | null => {
  const { machineId, teamId, children } = props;

  const { data: getMachineData } = useGetMachineQuery({
    variables: { input: { id: machineId, teamId } },
    skip: machineId === 'new',
  });

  const machine = getMachineData?.getMachine;

  const contextValue = useMemo<MachineContextValue | undefined>(() => {
    if (!machine) {
      return;
    }

    const isDirectConnect =
      machine.connectionType === MachineConnectionType.DIRECT;

    return {
      machine,
      isDirectConnect,
      hideAttachments: isDirectConnect,
      hideRecommendations: isDirectConnect,
    };
  }, [machine]);

  if (contextValue) {
    return (
      <MachineContext.Provider value={contextValue}>
        <ScratchpadPlaylistContextProvider machineId={machineId}>
          {children}
        </ScratchpadPlaylistContextProvider>
      </MachineContext.Provider>
    );
  }

  return null;
};

export const useMachineContext = (): MachineContextValue => {
  const context = useContext(MachineContext);
  return context as MachineContextValue;
};

export const useIsMachineActive = (): boolean => {
  const context = useContext(MachineContext);
  const active = context?.machine?.active;
  return active ? true : false;
};
